import React, { useEffect, useState, useCallback, useRef } from 'react';
import { View, Text, Alert, Platform, PermissionsAndroid, TouchableOpacity } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { WherebyEmbed, WherebyWebView } from '@whereby.com/react-native-sdk/embed';
import { WebViewMessageEvent } from 'react-native-webview';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { TRootStackParamList } from 'app/types/navigation';
import { api } from 'app/api/client';
import { useAuth } from 'app/auth/hooks/useAuth';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import Button from '@shared/components/md-design-system/Button/Button';
import { Icon } from '@ant-design/react-native';
import Popover, { PopoverPlacement } from 'react-native-popover-view';
import { colorPalette } from '@shared/config/colorsConfig';
import { getDoctorTitle } from '@shared/utils/doctor';
import { UploadDocumentModal, useDocumentUpload } from 'app/_modules/history';
import { ToastNotifications } from 'app/services/inAppToastNotifications';
import { APPOINTMENT_TYPE, TAppointmentType } from '../../constants/appointmentModality';
import { getWherebyInjectedJS } from './wherebyInjectedScripts';

const CALL_BG_COLOR = '#3c3c3c';

const buildWherebyUrl = (base: string): string => {
    const separator = base.includes('?') ? '&' : '?';
    const primaryColor = colorPalette.primary[600].replace('#', '');
    return `${base}${separator}primaryColor=${primaryColor}`;
};

type TWherebyDocumentMessage = {
    type: 'document';
    documentId: string;
    pathwayId: string;
    fileName: string;
    fileType: string;
    fileSize: number;
};

interface WherebyRoomProps {
    roomUrl: string;
    displayName?: string;
    onClose: () => void;
    appointmentType?: TAppointmentType;
    professional?: {
        fullName?: string;
        title?: string;
    };
}

export const WherebyRoom: React.FC<WherebyRoomProps> = ({
    roomUrl,
    displayName,
    onClose,
    professional,
    appointmentType = 'televisit',
}) => {
    const { t } = dynamicTranslations();
    const insets = useSafeAreaInsets();
    const navigation = useNavigation<NativeStackNavigationProp<TRootStackParamList>>();
    const [hasPermission, setHasPermission] = useState<boolean>(false);
    const { meData } = useAuth();
    const pathwayId = meData?.current_pathway?.id ?? '';
    const wherebyRef = useRef<WherebyWebView>(null);
    const upload = useDocumentUpload(pathwayId, {
        onBeforeCameraLaunch: () => wherebyRef.current?.toggleCamera(false),
        onAfterCameraLaunch: () => wherebyRef.current?.toggleCamera(true),
    });

    const appointmentLabel = appointmentType === APPOINTMENT_TYPE.TELEASSISTANCE ? 'Teleassistenza' : 'Televisita';
    const sessionTitle = !professional
        ? t('televisits.appointment')
        : professional.fullName
            ? `${appointmentLabel}\ncon ${getDoctorTitle(professional.title, t)} ${professional.fullName}`
            : appointmentLabel;

    const openDocument = useCallback(async (doc: TWherebyDocumentMessage) => {
        try {
            const data = await api.get<{ url: string }>(
                `rpm/api/v1/pathways/${doc.pathwayId}/documents/${doc.documentId}/download`,
            );
            navigation.navigate('DocumentViewerPage', {
                fileUrl: data.url,
                fileType: doc.fileType,
                fileName: doc.fileName,
            });
        } catch {
            ToastNotifications.displayErrorNotification(
                t('teleassistance.errors.documentNotAvailable'),
            );
        }
    }, [navigation]);

    const handleWebViewMessage = (event: WebViewMessageEvent) => {
        try {
            const parsed = JSON.parse(event.nativeEvent.data);
            if (parsed.__wherebyDocumentClick) {
                const doc = parsed.doc as TWherebyDocumentMessage;
                if (doc?.documentId) openDocument(doc);
            }
        } catch {
            // not a whereby message — ignore
        }
    };

    useEffect(() => {
        (async () => {
            if (Platform.OS === 'android') {
                try {
                    const permissions = await PermissionsAndroid.requestMultiple([
                        PermissionsAndroid.PERMISSIONS.CAMERA,
                        PermissionsAndroid.PERMISSIONS.RECORD_AUDIO,
                    ]);
                    const cameraGranted = permissions[PermissionsAndroid.PERMISSIONS.CAMERA] === PermissionsAndroid.RESULTS.GRANTED;
                    const micGranted = permissions[PermissionsAndroid.PERMISSIONS.RECORD_AUDIO] === PermissionsAndroid.RESULTS.GRANTED;
                    if (cameraGranted && micGranted) {
                        setHasPermission(true);
                    } else {
                        Alert.alert(
                            t('televisits.permissions.title'),
                            t('televisits.permissions.message'),
                            [{ text: t('commons.ok'), onPress: onClose }],
                        );
                        setHasPermission(false);
                    }
                } catch {
                    setHasPermission(false);
                }
            } else {
                setHasPermission(true);
            }
        })();
    }, []);

    if (Platform.OS === 'android' && !hasPermission) {
        return <View />;
    }

    return (
        <View className="flex-1" style={{ backgroundColor: CALL_BG_COLOR }}>
            <View
                className="flex-row items-center pb-3 px-5"
                style={{ paddingTop: insets.top + 8, backgroundColor: CALL_BG_COLOR }}
            >
                <View className="flex-1 items-start">
                    <Button onPress={onClose} variant='outline' size='small' icon='arrow-left'>
                        <Text className='text-base'>{t('buttons.back')}</Text>
                    </Button>
                </View>
                <View className="flex-1 items-center">
                    <Text className='text-white text-sm text-center' numberOfLines={2} ellipsizeMode='tail'>{sessionTitle}</Text>
                </View>
                <View className="flex-1 items-end">
                    <Popover
                        placement={PopoverPlacement.LEFT}
                        from={(
                            <TouchableOpacity>
                                <Icon name='safety-certificate' color={colorPalette.accent[100]} />
                            </TouchableOpacity>
                        )}
                    >
                        <Text className='p-3 rounded'>{t('televisits.security')}</Text>
                    </Popover>
                </View>
            </View>

            <WherebyEmbed
                ref={wherebyRef}
                style={{ flex: 1 }}
                room={buildWherebyUrl(roomUrl)}
                audio={true}
                chat={true}
                video={true}
                precallReview={false}
                screenshare={false}
                ignoreSilentHardwareSwitch={false}
                skipMediaPermissionPrompt={true}
                logo={false}
                leaveButton={false}
                topToolbar={false}
                lang="it"
                displayName={displayName}
                onLeave={onClose}
                injectedJavaScriptBeforeContentLoaded={getWherebyInjectedJS(t('televisits.chat.openChatButton'))}
                onMessage={handleWebViewMessage}
            />

            <TouchableOpacity
                onPress={() => upload.setIsUploadModalVisible(true)}
                className="absolute left-5 w-[52px] h-[52px] rounded-full bg-primary-600 justify-center items-center"
                style={{
                    bottom: insets.bottom + 24,
                    zIndex: 999,
                    elevation: 10,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 3 },
                    shadowOpacity: 0.3,
                    shadowRadius: 4,
                }}
            >
                <Icon name="upload" size={22} color={colorPalette.white} />
            </TouchableOpacity>

            <UploadDocumentModal
                visible={upload.isUploadModalVisible}
                onClose={() => upload.setIsUploadModalVisible(false)}
                onSelectCamera={upload.handleCameraUpload}
                onSelectExternalDevice={upload.handleExternalDeviceUpload}
            />
        </View>
    );
};
